import { useState } from 'react';
import { Bookmark } from 'lucide-react';
import clsx from 'clsx';
import { errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { ADD_LABEL, defaultShortcutLabel, readPageTitle, REMOVE_LABEL, SECTION_TITLE, useCurrentShortcutPath, useShortcutActions, useShortcuts } from './shortcuts';
import { ShortcutNameDialog } from './ShortcutDialogs';

/**
 * t607u: the bookmark in the desktop top bar. Not saved: opens a small dialog with the page's name (editable) and adds
 * the page to My Shortcuts. Saved: filled in the workspace's accent colour, and a click removes it.
 */
export function ShortcutButton() {
  const path = useCurrentShortcutPath();
  const q = useShortcuts();
  const actions = useShortcutActions();
  const toast = useUi(s => s.toast);
  const [adding, setAdding] = useState<string | null>(null);
  if (!path || !q.isSuccess) return null;
  const saved = q.data.shortcuts.find(s => s.path === path);
  const label = saved ? REMOVE_LABEL : ADD_LABEL;
  const click = async () => {
    if (!saved) { setAdding(defaultShortcutLabel(path, readPageTitle())); return; }
    try { await actions.remove(saved.id); toast(`Removed from ${SECTION_TITLE}`, 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <>
      <button type="button" onClick={() => void click()} title={label} aria-label={label} aria-pressed={Boolean(saved)} data-testid="shortcut-toggle"
        className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
        <Bookmark className={clsx('h-5 w-5', saved && 'fill-current text-slate-900 dark:text-slate-100')} />
      </button>
      {adding !== null && (
        <ShortcutNameDialog title={ADD_LABEL} initial={adding} onClose={() => setAdding(null)}
          onSave={async name => { await actions.add(path, name); toast(`Added to ${SECTION_TITLE}`, 'success'); }} />
      )}
    </>
  );
}
